// BingoGame.jsx
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, socket } from "../api";
import { parseJWT } from "../utils/jwtUtils";
import Footer from "../components/navbar/Footer";

export default function BingoGame() {
  const location = useLocation();
  const { roomInfo } = location.state || {};
  // console.log("room info : ", roomInfo);
  const roomId = roomInfo?.roomId;
  const token = localStorage.getItem("userInfo")
    ? JSON.parse(localStorage.getItem("userInfo"))
    : null;
  const userInfo = parseJWT(token?.token);

  const phone = userInfo?.phone;
  const navigate = useNavigate();
  // same bingo data & styles as you provided
  const bingoData = [
    { letter: "B", range: [1, 15], color: "bg-green-700" },
    { letter: "I", range: [16, 30], color: "bg-yellow-500" },
    { letter: "N", range: [31, 45], color: "bg-red-500" },
    { letter: "G", range: [46, 60], color: "bg-green-500" },
    { letter: "O", range: [61, 75], color: "bg-red-600" },
  ];

  // game state
  const [playerCards, setPlayerCards] = useState([]); // array of card grids (matrices)
  const [calledNumbers, setCalledNumbers] = useState([]); // global called numbers list
  const [currentNumber, setCurrentNumber] = useState(null); // most recent called
  const [ballsCalledCount, setBallsCalledCount] = useState(0);
  const [winnerResult, setWinnerResult] = useState(null); // result payload from server
  const [isGameEnded, setIsGameEnded] = useState(false);
  const [muted, setMuted] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [pendingNumber, setPendingNumber] = useState(null);

  // each player card will have marks by position (r,c)
  const [marksByCard, setMarksByCard] = useState({}); // { cardIndex: Set("r-c", ...) }

  // keep a ref to stop speech after winner
  const stopSpeakingRef = useRef(false);
  const cardsLoadedRef = useRef(false);
  const stateRestoredRef = useRef(false);
  // helper - determine bingo letter for a number
  const letterForNumber = (n) => {
    if (n >= 1 && n <= 15) return "B";
    if (n >= 16 && n <= 30) return "I";
    if (n >= 31 && n <= 45) return "N";
    if (n >= 46 && n <= 60) return "G";
    if (n >= 61 && n <= 75) return "O";
    return "";
  };

  const audioRef = useRef(new Audio());
  const mutedRef = useRef(muted);

  useEffect(() => {
    mutedRef.current = muted;
  }, [muted]);

  useEffect(() => {
    const unlock = () => {
      const audio = audioRef.current;
      audio.muted = true;
      audio.play().catch(() => {});
      audio.pause();
      audio.currentTime = 0;
      audio.muted = muted;
      document.removeEventListener("click", unlock);
    };

    document.addEventListener("click", unlock);
    return () => document.removeEventListener("click", unlock);
  }, []);

  useEffect(() => {
    const audio = audioRef.current;

    if (muted) {
      audio.pause();
      audio.currentTime = 0;
      audio.src = ""; // 🔴 cancels queued play
    }
  }, [muted]);

  const formatBingoAudioName = (num) => {
    const letter = letterForNumber(num);
    const padded = String(num).padStart(2, "0");
    return `/audio/Sounds/${letter}${padded}.mp3`;
  };

  const playBingoSound = (num) => {
    // 🔒 ABSOLUTE GUARD
    if (mutedRef.current || stopSpeakingRef.current) return;

    const audio = audioRef.current;

    // Cancel anything pending
    audio.pause();
    audio.currentTime = 0;

    // Guard again (race condition protection)
    if (mutedRef.current) return;

    audio.src = formatBingoAudioName(num);
    audio.muted = false;
    audio.volume = 1;

    const playPromise = audio.play();

    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // autoplay / interruption safe
      });
    }
  };

  // toggle mark on player's card cell (player marks manually)
  const toggleMark = (cardIndex, rowIdx, colIdx, num) => {
    if (isGameEnded) return;
    // only allow marking if this number has been called or is Free space
    if (num !== "F" && !isCalled(num)) return;
    setMarksByCard((prev) => {
      const newObj = { ...prev };
      const key = `${rowIdx}-${colIdx}`;
      const setForCard = new Set(newObj[cardIndex] || []);
      if (setForCard.has(key)) setForCard.delete(key);
      else setForCard.add(key);
      newObj[cardIndex] = setForCard;
      return newObj;
    });
  };

  // helper - check whether a number is called globally
  const isCalled = (num) => calledNumbers.includes(num);
  // Play local bingo sound

  const playBingoSoundFinal = () => {
    if (muted) return;

    const audio = audioRef.current;
    audio.pause();
    audio.currentTime = 0;

    audio.src = "/audio/Sounds/bingo.mp3";
    audio.muted = muted;
    audio.volume = muted ? 0 : 1;
    audio.play().catch(() => {});
  };

  const normalizeGrid = (grid) => {
    if (!grid) return [];

    try {
      // keep parsing until it's an actual array
      while (typeof grid === "string") {
        grid = JSON.parse(grid);
      }
    } catch {
      return []; // invalid JSON
    }

    // if object with numeric keys, convert to array
    if (!Array.isArray(grid) && typeof grid === "object") {
      grid = Object.values(grid);
    }

    // if flat 25 element array, convert to 5x5
    if (Array.isArray(grid) && grid.length === 25 && !Array.isArray(grid[0])) {
      const out = [];
      for (let i = 0; i < 25; i += 5) out.push(grid.slice(i, i + 5));
      grid = out;
    }

    // validate 5x5
    if (
      Array.isArray(grid) &&
      grid.length === 5 &&
      grid.every((r) => Array.isArray(r) && r.length === 5)
    ) {
      return grid;
    }

    return [];
  };

  const loadedRef = useRef(false);

  useEffect(() => {
    if (!roomId || !phone || loadedRef.current) return;
    loadedRef.current = true; // ✅ prevent duplicate load

    const load = async () => {
      try {
        // console.log("⏳ Loading player cards for", phone, "in room", roomId);
        const res = await api.get(`/api/playercards/${phone}/${roomId}`);
        // console.log("✅ [LOAD] Raw cards from API:", cards);

        const cards = (res.data && res.data.cards) || [];
        // console.log("🧩 Extracted cards array:", cards);

        const formattedCards = cards.map((c) => {
          const rawGrid = c.grid || c.cardGrid || c.card || [];

          const normalizedGrid = normalizeGrid(rawGrid);

          return {
            grid: normalizedGrid, // ✅ ALWAYS 5x5 now
            cardNumber: c.selectedCardNumber || c.cardNumber || null,
          };
        });

        // console.log("✅ [LOAD] Normalized player cards:", formattedCards);

        const selectedCard =
          cards.find((c) => c.selectedCardNumber)?.selectedCardNumber || null;

        setPlayerCards(formattedCards);
        setSelectedCard(selectedCard);

        const marks = {};
        formattedCards.forEach((_, idx) => (marks[idx] = new Set()));
        //  console.log("✅ [LOAD] Initial marksByCard (empty):", marks);
        setMarksByCard(marks);
        //  console.log("✅ Initialized marksByCard:", marks);
      } catch (err) {
        //  console.error("❌ Error loading player cards:", err);
        setPlayerCards([]);
      }
    };

    load();
  }, [roomId, phone]);

  /* ============================
     Socket: join room + listeners
     ============================ */
  useEffect(() => {
    if (!roomId || !phone) return;
    stopSpeakingRef.current = false;
    setIsGameEnded(false);
    setWinnerResult(null);

    // --- Restore state after refresh ---
    const restoreAndResume = async () => {
      try {
        const res = await api.get(`/api/room-state/${roomId}/${phone}`);
        const data = res.data || {};

        const calledNumbers = Array.isArray(data.calledNumbers)
          ? data.calledNumbers
          : [];
        const currentNumber =
          data.currentNumber ??
          (calledNumbers.length
            ? calledNumbers[calledNumbers.length - 1]
            : null);

        setCalledNumbers(calledNumbers);
        setCurrentNumber(currentNumber);
        setBallsCalledCount(data.ballsCalledCount || 0);

        // Restore player cards
        if (Array.isArray(data.playerCards)) {
          const formattedCards = data.playerCards.map((c) => ({
            grid: normalizeGrid(c.grid || c.cardGrid || c.card),
            cardNumber: c.selectedCardNumber || c.cardNumber || null,
          }));

          setPlayerCards(formattedCards);

          // Restore marks
          const marks = {};
          formattedCards.forEach((card, idx) => {
            const set = new Set();
            card.grid.forEach((row, r) =>
              row.forEach((num, c) => {
                if (num === "F" || calledNumbers.includes(num))
                  set.add(`${r}-${c}`);
              }),
            );
            marks[idx] = set;
          });
          setMarksByCard(marks);
        }

        // Play last called number
        if (currentNumber != null) playBingoSound(Number(currentNumber));

        // Resume number calling from backend
        socket.emit("resume-number-calling", { roomId });
      } catch (err) {
        console.error("❌ restoreAndResume failed:", err);
      }
    };

    restoreAndResume();

    // --- Existing number handler ---
    const handleNumber = (payload) => {
      try {
        let num = null;
        let rid = null;
        let drawnList = [];

        if (payload && typeof payload === "object") {
          num =
            payload.ball ??
            payload.number ??
            payload.num ??
            payload.value ??
            payload.currentNumber ??
            payload.calledNumber ??
            payload.numberCalled ??
            payload.data;
          rid = payload.roomId || payload.room || payload.room_id;
          drawnList = payload.drawn || [];
        } else if (typeof payload === "number" || typeof payload === "string") {
          num = Number(payload);
        }

        if (rid && rid !== roomId) return;
        if (num == null || isNaN(num)) return;
        setCurrentNumber(num);
        setBallsCalledCount((c) => c + 1);

        // ✅ ADD NUMBER TO STATE IMMEDIATELY (truth)
        setCalledNumbers((prev) => {
          if (prev.includes(num)) return prev;
          return [...prev, num];
        });

        // Sound still delayed
        playBingoSound(Number(num));
        setPendingNumber(num);
      } catch (e) {
        console.error("❌ handleNumber error:", e);
      }
    };

    socket.on("number-called", handleNumber);
    socket.on("new-number", handleNumber);
    socket.on("number", handleNumber);
    socket.on("bingo-number", handleNumber);
    socket.on("new-ball", handleNumber);

    const onGameStarted = ({ roomId: rid }) => {
      if (rid !== roomId) return;

      setCalledNumbers([]);
      setCurrentNumber(null);
      setBallsCalledCount(0);
      setIsGameEnded(false);
      stopSpeakingRef.current = false;

      api
        .get(`/api/playercards/${phone}/${roomId}`)
        .then((res) => {
          const cards = (res.data && res.data.cards) || [];

          const formattedCards = cards.map((c) => ({
            grid: normalizeGrid(c.grid || c.cardGrid || c.card),
            cardNumber: c.selectedCardNumber || c.cardNumber || null,
          }));

          setPlayerCards(formattedCards);

          const marks = {};
          formattedCards.forEach((_, idx) => (marks[idx] = new Set()));
          setMarksByCard(marks);
        })
        .catch(() => {});
    };

    socket.on("game-started", onGameStarted);
    socket.on("room-status", ({ roomId: rid, status }) => {
      if (rid !== roomId) return;
    });

    const onWinnerDeclared = (payload) => {
      const rid = payload.roomId || payload.room;
      if (rid && rid !== roomId) return;

      stopSpeakingRef.current = true;
      if (window.speechSynthesis) window.speechSynthesis.cancel();

      // 🔊 play bingo sound first
      playBingoSoundFinal();

      // ⏳ show winner AFTER sound
      setTimeout(() => {
        setWinnerResult(payload);
        setIsGameEnded(true);
      }, 2400); // length of bingo.mp3
    };

    socket.on("winner-declared", onWinnerDeclared);
    socket.on("invalid-win", (msg) => {
      // console.warn("Invalid bingo claim:", msg);
    });

    return () => {
      socket.off("number-called", handleNumber);
      socket.off("new-number", handleNumber);
      socket.off("number", handleNumber);
      socket.off("bingo-number", handleNumber);
      socket.off("new-ball", handleNumber);

      socket.off("game-started", onGameStarted);
      socket.off("winner-declared", onWinnerDeclared);
      socket.off("invalid-win");

      stopSpeakingRef.current = true;
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    };
  }, [roomId, phone]);

  useEffect(() => {
    if (!playerCards.length || !calledNumbers.length) return;

    // console.log("📝 [EFFECT] playerCards or calledNumbers changed");
    // console.log("📝 Current marksByCard before merge:", marksByCard);
    // console.log("📝 Called numbers:", calledNumbers);

    setMarksByCard((prev) => {
      const merged = { ...prev };
      playerCards.forEach((card, cardIndex) => {
        const existingSet = new Set(merged[cardIndex] || []);
        card.grid.forEach((row, r) => {
          row.forEach((num, c) => {
            if (num === "F" || calledNumbers.includes(num)) {
              existingSet.add(`${r}-${c}`);
            }
          });
        });
        merged[cardIndex] = existingSet;
        // console.log(
        //   `📝 [EFFECT] marksByCard[${cardIndex}] after merge:`,
        //   Array.from(existingSet)
        // );
      });
      return merged;
    });
  }, [playerCards, calledNumbers]);

  // UI helpers for highlighting the current number
  const numberBoxClasses = (num) =>
    num === currentNumber
      ? "bg-green-500 shadow-lg scale-105"
      : isCalled(num)
        ? "bg-green-500 opacity-90"
        : "bg-[#0e3e3d] hover:bg-[#14504d] transition";

  /* ============================
     Render (keeps your exact styles)
     ============================ */
  const getWinningPositions = (grid, calledNumbers) => {
    if (!grid.length) return new Set();

    const isMarked = (r, c) => {
      const val = grid[r][c];
      return val === "F" || calledNumbers.includes(val);
    };

    const wins = new Set();

    // rows
    for (let r = 0; r < 5; r++) {
      if ([0, 1, 2, 3, 4].every((c) => isMarked(r, c))) {
        for (let c = 0; c < 5; c++) wins.add(`${r}-${c}`);
      }
    }

    // columns
    for (let c = 0; c < 5; c++) {
      if ([0, 1, 2, 3, 4].every((r) => isMarked(r, c))) {
        for (let r = 0; r < 5; r++) wins.add(`${r}-${c}`);
      }
    }

    // diagonal ↘
    if ([0, 1, 2, 3, 4].every((i) => isMarked(i, i))) {
      for (let i = 0; i < 5; i++) wins.add(`${i}-${i}`);
    }

    // diagonal ↙
    if ([0, 1, 2, 3, 4].every((i) => isMarked(i, 4 - i))) {
      for (let i = 0; i < 5; i++) wins.add(`${i}-${4 - i}`);
    }
    // 🟡 ✅ FOUR CORNERS (FIX)
    const corners = [
      [0, 0],
      [0, 4],
      [4, 0],
      [4, 4],
    ];

    if (corners.every(([r, c]) => isMarked(r, c))) {
      corners.forEach(([r, c]) => wins.add(`${r}-${c}`));
    }
    return wins;
  };

  const joinAndSubscribeRoom = () => {
    if (!roomId || !phone) return;

    const amount = roomInfo?.amount; // ✅ IMPORTANT

    socket.emit("join-room", { roomId, phone, amount }, (res) => {
      //  console.log("🧩 [JOIN RESPONSE]:", res);

      if (res?.error) {
        console.error("❌ Join failed:", res.error);
        return;
      }

      if (res?.ok) {
        //  console.log("✅ Successfully rejoined room", roomId);
      }
    });

    socket.emit("subscribe-number-feed", { roomId });
  };

  useEffect(() => {
    if (!roomId || !phone || stateRestoredRef.current) return;
    stateRestoredRef.current = true;

    const restoreGameState = async () => {
      try {
        // console.log(
        //   `⏳ [RESTORE] Fetching room state for ${phone} room ${roomId}`
        // );
        const res = await api.get(`/api/room-state/${roomId}/${phone}`);
        const data = res.data || {};
        // console.log("✅ [RESTORE] Raw room-state API:", data);
        const calledNumbers = Array.isArray(data.calledNumbers)
          ? data.calledNumbers
          : [];
        const currentNumber = data.currentNumber || null;
        const ballsCalledCount = data.ballsCalledCount || 0;
        const cards = Array.isArray(data.playerCards) ? data.playerCards : [];

        // console.log("✅ [RESTORE] Called numbers from API:", calledNumbers);
        // console.log("✅ [RESTORE] Current number from API:", currentNumber);
        // console.log("✅ [RESTORE] Balls called count:", ballsCalledCount);
        // console.log("✅ [RESTORE] Player cards raw:", cards);

        const formattedCards = cards.map((c, idx) => {
          const grid = normalizeGrid(c.grid || c.cardGrid || c.card || []);
          //  console.log(`🧩 [RESTORE] Card ${idx} normalized grid:`, grid);
          return {
            grid,
            cardNumber: c.selectedCardNumber || c.cardNumber || null,
          };
        });
        //  console.log("✅ [RESTORE] Normalized cards:", formattedCards);

        setPlayerCards(formattedCards);
        setCalledNumbers(calledNumbers);
        setCurrentNumber(
          currentNumber ||
            (calledNumbers.length
              ? calledNumbers[calledNumbers.length - 1]
              : null),
        );

        setBallsCalledCount(ballsCalledCount);

        // marksByCard debug
        const marks = {};
        formattedCards.forEach((card, idx) => {
          const set = new Set();
          card.grid.forEach((row, r) => {
            row.forEach((num, c) => {
              if (num === "F" || calledNumbers.includes(num)) {
                set.add(`${r}-${c}`);
              }
            });
          });
          marks[idx] = set;
          // console.log(
          //   `📝 [RESTORE] marksByCard[${idx}] calculated:`,
          //   Array.from(set)
          // );
        });
        setMarksByCard(marks);
        //  console.log("✅ [RESTORE] Restored marksByCard:", marks);

        if (currentNumber) {
          // console.log(
          //   "🎵 [RESTORE] Playing current number sound:",
          //   currentNumber
          // );
          playBingoSound(Number(currentNumber));
        }

        // Rejoin room for live updates
        // console.log("🚀 [RESTORE] Rejoining room via socket...");
        joinAndSubscribeRoom();

        // 🔹 NEW: Resume number calling if game is playing
        if (data.status === "playing" && (!currentNumber || ballsCalledCount)) {
          // console.log(
          //   "🚀 [RESTORE] Resuming number-calling loop after refresh..."
          // );
          socket.emit("resume-number-calling", { roomId });
        }
      } catch (err) {
        console.error("❌ [RESTORE] restoreGameState failed:", err);
      }
    };

    restoreGameState();
  }, [roomId, phone]);

  useEffect(() => {
    if (!roomId || !phone || stateRestoredRef.current) return;
    stateRestoredRef.current = true;

    // Join room
    socket.emit("join-room", { roomId, phone }, (res) => {
      if (res?.ok) {
        const cards = res.playerCards.map((c) => ({
          grid: typeof c.grid === "string" ? JSON.parse(c.grid) : c.grid,
          cardNumber: c.cardNumber,
          selectedCardNumber: c.selectedCardNumber,
        }));
        setPlayerCards(cards);
      }
    });

    socket.on("sync-game-state", (data) => {
      // console.log("🔄 [SOCKET] sync-game-state received:", data);
      setCalledNumbers(data.calledNumbers || []);
      setCurrentNumber(data.currentNumber);
      setBallsCalledCount(data.ballsCalledCount || 0);

      const marks = {};
      data.playerCards.forEach((card, idx) => {
        const set = new Set();
        card.grid.forEach((row, r) => {
          row.forEach((num, c) => {
            if (num === "F" || (data.calledNumbers || []).includes(num))
              set.add(`${r}-${c}`);
          });
        });
        marks[idx] = set;
        // console.log(
        //   `📝 [SOCKET] marksByCard[${idx}] calculated:`,
        //   Array.from(set)
        // );
      });
      setMarksByCard(marks);
    });

    // Restore marks separately if backend emits them
    socket.on("restore-marks", ({ marks }) => {
      const restored = {};
      Object.keys(marks).forEach((cardNumber) => {
        restored[cardNumber] = new Set(marks[cardNumber]);
      });
      setMarksByCard(restored);
    });

    // Clean up listeners on unmount
    return () => {
      socket.off("sync-game-state");
      socket.off("restore-marks");
    };
  }, [roomId, phone]);

  useEffect(() => {
    if (!roomId || !phone) return;

    const handleRestoreGame = ({
      calledNumbers,
      currentNumber,
      marksByCard,
    }) => {
      // console.log("🔄 [SOCKET] restore-game received:", {
      //   calledNumbers,
      //   currentNumber,
      //   marksByCard,
      // });

      setCalledNumbers(calledNumbers || []);
      setCurrentNumber(currentNumber || (calledNumbers?.slice(-1)[0] ?? null));

      const marks = {};
      if (playerCards.length) {
        playerCards.forEach((_, idx) => {
          const cardMarks = marksByCard[idx] || [];
          marks[idx] = new Set(cardMarks);
        });
      }
      setMarksByCard(marks);

      // Play current number sound if there is one
      if (currentNumber) playBingoSound(Number(currentNumber));
    };

    socket.on("restore-game", handleRestoreGame);

    return () => socket.off("restore-game", handleRestoreGame);
  }, [roomId, phone, playerCards]);

  // console.log("bingo card detail : ", playerCards);
  const lastFourNumbers = calledNumbers.slice(-4);

  const handleHome = () => {
    navigate("/"); // or your home route
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleInvite = async () => {
    const inviteLink = `${window.location.origin}/join/${roomId}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join my Bingo Game 🎯",
          text: "Join my Bingo room and play!",
          url: inviteLink,
        });
      } catch (err) {}
    } else {
      await navigator.clipboard.writeText(inviteLink);
      alert("Invite link copied!");
    }
  };

  return (
    <div className="min-h-screen bg-primary text-white p-2 sm:p-4 flex flex-col items-center">
      {/* --- PC Layout --- */}
      <div className="hidden lg:flex w-full max-w-7xl justify-between mt-4 gap-6">
        {/* Left Section - Current Ball */}
        <div className="bg-[#134454] p-4 rounded-xl shadow-lg flex-1 max-w-md relative">
          <div className="flex flex-col items-center justify-center mb-6 relative">
            <div className="relative flex items-center justify-center w-36 h-36 rounded-full bg-gradient-to-b from-green-200 to-green-800 border-4 border-green-700 shadow-inner">
              <div className="absolute top-2 text-lg font-semibold">
                {currentNumber ? letterForNumber(currentNumber) : "I"}
              </div>
              <div className="text-5xl font-extrabold text-white">
                {currentNumber ?? "--"}
              </div>
            </div>
            <div className="absolute right-16 top-20 w-10 h-10 rounded-full bg-[#c47c3f] border-4 border-[#73441c] flex items-center justify-center text-xl font-bold text-white shadow-lg">
              {ballsCalledCount}
            </div>
          </div>

          <div className="flex items-center justify-between mt-4 px-4">
            <div className="flex flex-col items-center gap-2">
              {/* Last 4 called numbers */}
              <div className="flex gap-1 mt-1">
                {lastFourNumbers?.length > 0 ? (
                  lastFourNumbers?.map((num, i) => (
                    <div
                      key={i}
                      className="w-8 h-8 rounded-full bg-[#c47c3f] border-4 border-[#73441c] flex items-center justify-center text-sm font-bold text-white shadow-lg"
                    >
                      {num}
                    </div>
                  ))
                ) : (
                  <p className="text-[10px] text-gray-400 font-serif">
                    No calls yet
                  </p>
                )}
              </div>
              <p className="text-xs font-semibold font-serif">Balls Called</p>
            </div>

            <div
              className="text-2xl cursor-pointer"
              title={muted ? "Unmute" : "Mute"}
              onClick={() => setMuted((m) => !m)}
            >
              {muted ? "🔇" : "🔊"}
            </div>
          </div>
        </div>

        {/* Center Section - Number List */}
        <div className="flex-1 flex flex-col items-center">
          <div className="bg-[#134454] rounded-lg shadow-xl w-full max-w-4xl overflow-hidden">
            <div className="bg-gradient-to-r from-[#115971] to-[#2c4863] text-center py-3 relative">
              <h1 className="text-lg font-semibold font-serif">Good Luck</h1>
              <p className="absolute top-3 right-6 text-sm flex items-center gap-1">
                <span className="text-white">👤</span> {roomInfo?.totalCards}
              </p>
            </div>

            <div className="p-4 hidden lg:flex items-start gap-4">
              {/* BINGO letters */}
              <div className="flex flex-col space-y-2">
                {bingoData?.map((b) => (
                  <div
                    key={b.letter}
                    className={`${b.color} w-10 h-10 flex items-center justify-center rounded-full font-bold text-white text-lg font-serif`}
                  >
                    {b.letter}
                  </div>
                ))}
              </div>

              {/* Numbers beside each letter */}
              <div className="flex flex-col space-y-2 flex-1">
                {bingoData?.map((b) => (
                  <div key={b.letter} className="flex gap-2">
                    {Array.from({ length: 15 }, (_, i) => {
                      const num = b.range[0] + i;
                      return (
                        <div
                          key={num}
                          className={`w-10 h-10 flex items-center justify-center rounded-md font-semibold bg-[#073440] text-[#e6f7f9] text-sm font-serif ${numberBoxClasses(
                            num,
                          )}`}
                        >
                          {num}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Desktop Card Grid (4 cards per row) */}
      <div className="hidden lg:grid mt-8 bg-[#134454] rounded-2xl p-4 w-full shadow-lg text-center gap-6 grid-cols-4">
        {playerCards?.length === 0 ? (
          <div className="bg-gradient-to-b from-[#115971] to-[#2c4863] p-3 rounded-xl shadow-lg col-span-4">
            <h1 className="text-gray-300 font-serif py-2">Watching Only</h1>
            <h2>Waiting</h2>
          </div>
        ) : (
          playerCards?.map((card, cardIndex) => {
            const { grid, cardNumber } = card;

            return (
              <div
                key={cardIndex}
                className="bg-[#1e4e62] rounded-xl p-4 shadow-lg"
              >
                {/* Card Header */}
                <h2 className="font-bold text-lg mb-4 font-serif">
                  Card No {cardNumber || cardIndex + 1}
                </h2>

                {/* Letters Row */}
                <div className="grid grid-cols-5 gap-2 mb-3 font-serif">
                  {["B", "I", "N", "G", "O"].map((letter, i) => (
                    <div
                      key={i}
                      className={`flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full font-bold text-white mx-auto ${
                        letter === "B"
                          ? "bg-green-700"
                          : letter === "I"
                            ? "bg-yellow-500"
                            : letter === "N"
                              ? "bg-red-500"
                              : letter === "G"
                                ? "bg-green-500"
                                : "bg-red-500"
                      }`}
                    >
                      {letter}
                    </div>
                  ))}
                </div>

                {/* Numbers Grid */}
                <div className="grid grid-cols-5 gap-2">
                  {Array.isArray(grid) &&
                    grid.length === 5 &&
                    grid.every((r) => Array.isArray(r) && r.length === 5) &&
                    grid.map((row, rowIndex) =>
                      row.map((num, colIndex) => {
                        const key = `${rowIndex}-${colIndex}`;
                        const marked =
                          marksByCard[cardIndex] &&
                          marksByCard[cardIndex].has(key);
                        const called = isCalled(num);

                        return (
                          <div
                            key={key}
                            onClick={() =>
                              toggleMark(cardIndex, rowIndex, colIndex, num)
                            }
                            className={`w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-lg font-semibold text-sm sm:text-base cursor-pointer select-none ${
                              num === "F"
                                ? "bg-[#73441c] text-white"
                                : marked || called
                                  ? "bg-blue-800 text-white"
                                  : "bg-white text-black"
                            }`}
                          >
                            {num === "F" ? "★" : num}
                          </div>
                        );
                      }),
                    )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* --- MOBILE Layout (Updated with Header + Card Under Current Ball) --- */}
      <div className="w-full lg:hidden mt-4 flex flex-col items-center gap-3">
        {/* 🔹 Mobile Header */}
        <div className="w-full bg-gradient-to-r from-[#115971] to-[#2c4863] text-white flex justify-around items-center py-2 rounded-lg shadow-md text-md font-semibold">
          <div className="flex flex-col items-center font-serif">
            <span>Win</span>
            <span className="text-xs text-green-400 font-bold font-serif">
              {roomInfo?.winAmount ?? "—"}ETB
            </span>
          </div>
          <div className="flex flex-col items-center font-serif">
            <span>Stack</span>
            <span className="text-xs text-yellow-400">{roomInfo?.amount}</span>
          </div>

          {/* <div className="absolute right-16 top-20 w-10 h-10 rounded-full bg-[#c47c3f] border-4 border-[#73441c] flex items-center justify-center text-xl font-bold text-white shadow-lg">
              {ballsCalledCount}
            </div> */}
          <div className="flex flex-col items-center font-serif">
            <span>Player</span>
            <span className="text-sm text-gray-300">
              {roomInfo?.totalCards}
            </span>
          </div>
          <div className="flex flex-col items-center font-serif">
            <span>Call</span>
            <span className="text-sm text-blue-300">{ballsCalledCount}</span>
          </div>
          <div
            className="flex flex-col items-center cursor-pointer"
            onClick={() => setMuted((m) => !m)}
            title={muted ? "Unmute" : "Mute"}
          >
            <span className="text-lg">{muted ? "🔇" : "🔊"}</span>
          </div>
        </div>

        {/* 🔹 Game Body (Number List + Current Ball + Card) */}
        <div className="flex flex-row gap-2 justify-center items-start w-full">
          {/* Left: Number List */}
          <div className="flex-1 bg-[#134454] rounded-lg shadow-xl overflow-visible max-w-[250px]">
            <div className="p-1">
              {/* Letters row */}
              <div className="flex justify-between mb-2">
                {bingoData?.map((b) => (
                  <div
                    key={b.letter}
                    className={`${b.color} w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center rounded-full font-bold text-white text-sm`}
                  >
                    {b.letter}
                  </div>
                ))}
              </div>

              {/* Numbers grid */}
              <div className="grid grid-cols-5 gap-x-[1px] gap-y-[2px] text-center justify-items-center">
                {Array.from({ length: 15 })?.map((_, rowIndex) =>
                  bingoData?.map((b) => {
                    const num = b.range[0] + rowIndex;
                    return (
                      <div
                        key={`${b.letter}-${num}`}
                        className={`w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center rounded-md font-semibold bg-[#073440] text-[#e6f7f9] text-xs ${numberBoxClasses(
                          num,
                        )}`}
                      >
                        {num}
                      </div>
                    );
                  }),
                )}
              </div>
            </div>
          </div>

          {/* Right: Current Ball + Card vertically stacked */}
          <div className="flex flex-col items-center gap-4 w-[180px] overflow-visible">
            {/* Current Ball */}
            <div className="bg-[#134454] p-3 rounded-xl shadow-lg w-full">
              <div className="flex flex-col items-center justify-center mb-4 relative">
                <div className="relative flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-b from-green-200 to-green-800 border-4 border-green-700 shadow-inner">
                  <div className="absolute top-1 text-sm font-semibold">
                    {currentNumber ? letterForNumber(currentNumber) : "B"}
                  </div>
                  <div className="text-3xl font-extrabold text-white">
                    {currentNumber ?? "--"}
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-center gap-2">
                <p className="text-xs font-semibold font-serif">Balls Called</p>

                {/* Last 4 called numbers */}
                <div className="flex gap-1 mt-1">
                  {lastFourNumbers?.length > 0 ? (
                    lastFourNumbers?.map((num, i) => (
                      <div
                        key={i}
                        className="w-8 h-8 rounded-full bg-[#c47c3f] border-4 border-[#73441c] flex items-center justify-center text-sm font-bold text-white shadow-lg"
                      >
                        {num}
                      </div>
                    ))
                  ) : (
                    <p className="text-[10px] text-gray-400 font-serif">
                      No calls yet
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Card Section (Under Current Ball) */}
            {playerCards?.length > 0 ? (
              playerCards?.map((card, cardIndex) => {
                const { grid, cardNumber } = card;

                // ✅ Safely handle undefined or flat grids
                const isValidGrid =
                  Array.isArray(grid) &&
                  grid.length === 5 &&
                  grid.every((row) => Array.isArray(row) && row.length === 5);

                return (
                  <div
                    key={cardIndex}
                    className="bg-[#3b5772] rounded-2xl p-3 w-full shadow-lg mb-2"
                  >
                    {/* ✅ Card Header */}
                    <h2 className="text-center font-bold text-sm mb-2 text-white font-serif">
                      Card No. {""}
                      {cardNumber || cardIndex + 1}
                    </h2>

                    {/* ✅ BINGO Header Row */}
                    <div className="grid grid-cols-5 gap-1 mb-2 text-center font-serif">
                      {["B", "I", "N", "G", "O"]?.map((letter, i) => (
                        <div
                          key={i}
                          className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-white text-xs mx-auto ${
                            letter === "B"
                              ? "bg-green-700"
                              : letter === "I"
                                ? "bg-yellow-500"
                                : letter === "N"
                                  ? "bg-red-500"
                                  : letter === "G"
                                    ? "bg-green-500"
                                    : "bg-red-500"
                          }`}
                        >
                          {letter}
                        </div>
                      ))}
                    </div>

                    {/* ✅ Safe Number Grid */}
                    <div className="grid grid-cols-5 gap-1">
                      {Array.isArray(grid) &&
                        grid.length === 5 &&
                        grid.every((r) => Array.isArray(r) && r.length === 5) &&
                        grid.map((row, rowIndex) =>
                          row.map((num, colIndex) => {
                            const key = `${rowIndex}-${colIndex}`;
                            const marked =
                              marksByCard[cardIndex] &&
                              marksByCard[cardIndex].has(key);
                            const called = isCalled(num);

                            return (
                              <div
                                key={key}
                                onClick={() =>
                                  toggleMark(cardIndex, rowIndex, colIndex, num)
                                }
                                className={`w-7 h-7 sm:w-12 sm:h-12 flex items-center justify-center rounded-lg font-semibold text-sm sm:text-base cursor-pointer select-none ${
                                  num === "F"
                                    ? "bg-[#73441c] text-white"
                                    : marked || called
                                      ? "bg-blue-800 text-white"
                                      : "bg-white text-black"
                                }`}
                              >
                                {num === "F" ? "★" : num}
                              </div>
                            );
                          }),
                        )}
                    </div>

                    {/* Optional fallback message if grid is broken */}
                    {!isValidGrid && grid?.length > 0 && (
                      <div className="text-center text-xs text-gray-300 mt-2 font-serif">
                        ⚠️ Invalid grid format
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className=" bg-[#134454] p-3 rounded-xl shadow-lg w-full">
                <h1 className="text-gray-300 font-serif py-2">Watching Only</h1>
                <h2>Waiting</h2>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Winner / Loser Full-Screen Modal */}
      {isGameEnded &&
        winnerResult &&
        (() => {
          // ===============================
          // ✅ MULTI-WINNER SAFE CHECK
          // ===============================
          const winnerPhones = Array.isArray(winnerResult.winnerPhone)
            ? winnerResult.winnerPhone
            : [winnerResult.winnerPhone];

          const winnerCardNumbers = Array.isArray(winnerResult.winnerCardNumber)
            ? winnerResult.winnerCardNumber
            : [winnerResult.winnerCardNumber];

          const winnerGrids = Array.isArray(winnerResult.winnerGrid?.[0]?.[0])
            ? // MULTI-WINNER → array of 5x5 grids
              winnerResult.winnerGrid
            : // SINGLE-WINNER → wrap the 5x5 grid
              [winnerResult.winnerGrid];

          const isWinner = winnerPhones.some(
            (p) => String(p) === String(phone),
          );
          const formatWinnerName = (name) => {
            if (Array.isArray(name)) return name.join(", ");
            return name || "Unknown";
          };
          // ===============================
          // ✅ PREPARE WINNER CARD GRIDS
          // ===============================
          let displayGrids = [];

          const myWinnerIndex = winnerPhones.findIndex(
            (p) => String(p) === String(phone),
          );

          if (myWinnerIndex !== -1) {
            // 🏆 YOU are a winner → show ALL winner cards
            displayGrids = winnerPhones.map((_, i) => {
              return (
                playerCards.find(
                  (c) => String(c.cardNumber) === String(winnerCardNumbers[i]),
                )?.grid ||
                normalizeGrid(winnerGrids[i]) ||
                []
              );
            });
          } else {
            // 😭 You lost → show ALL winner cards
            displayGrids = winnerGrids.map((g) => normalizeGrid(g) || []);
          }
          // console.log(
          //   "Normalized Grids:",
          //   winnerGrids.map((g) => normalizeGrid(g)),
          // );
          // console.log("winner info 2: ", winnerResult);
          return (
            <div className="fixed inset-0 bg-black bg-opacity-80 flex justify-center items-center z-50">
              <div
                className={`rounded-2xl shadow-lg p-8 w-80 sm:w-96 text-center ${
                  isWinner ? "bg-green-700" : "bg-red-700"
                }`}
              >
                {/* Emoji */}
                <div className="text-6xl mb-4">{isWinner ? "😄" : "😭"}</div>

                {/* Title */}
                <h2 className="text-3xl font-bold mb-2 text-white">
                  {isWinner ? "You Won!" : "You Lost"}
                </h2>

                {/* Amount */}
                <p className="text-lg text-white font-semibold mb-2 font-serif">
                  Amount: {winnerResult.winAmount} ETB
                </p>
                {/*    <p className="text-lg text-white font-semibold mb-2 font-serif">
                  Winner Name: {formatWinnerName(winnerResult?.winnerName)}
                </p>
                */}
                <div
                  className={`bg-white text-black rounded-xl p-4 mb-6 w-full border-4 overflow-y-auto max-h-[60vh]
    ${
      isWinner
        ? "border-yellow-400 shadow-yellow-400 shadow-lg"
        : "border-gray-300"
    }
  `}
                >
                  <div className="space-y-6">
                    {displayGrids.map((grid, cardIndex) => {
                      // ✅ Use REAL called numbers first
                      const numbersForCheck =
                        calledNumbers && calledNumbers.length
                          ? calledNumbers
                          : winnerResult.winningNumbers || [];

                      const winningPositions = getWinningPositions(
                        grid,
                        numbersForCheck,
                      );
                      const isMyCard = isWinner && cardIndex === myWinnerIndex;

                      return (
                        <div key={cardIndex}>
                          {/* Card label */}
                          <p className="text-center font-semibold mb-2">
                            Winner Card #{winnerCardNumbers[cardIndex]}
                            {isMyCard && (
                              <span className="ml-2 text-green-600 font-bold">
                                ⭐ Your Card
                              </span>
                            )}
                          </p>

                          {/* Bingo grid */}
                          <div className="grid grid-cols-5 gap-2 text-center font-semibold">
                            {["B", "I", "N", "G", "O"].map((l) => (
                              <div
                                key={l}
                                className="text-yellow-600 font-bold"
                              >
                                {l}
                              </div>
                            ))}

                            {grid.flat().map((num, i) => {
                              const row = Math.floor(i / 5);
                              const col = i % 5;
                              const key = `${row}-${col}`;
                              const isFree = num === "F";
                              const called = calledNumbers.includes(num);
                              const isWinnerCell = winningPositions.has(key);

                              return (
                                <div
                                  key={i}
                                  className={`w-8 h-8 flex items-center justify-center rounded font-semibold
                            ${
                              isFree
                                ? "bg-blue-500 text-white"
                                : called
                                  ? "bg-gray-700 text-white"
                                  : "bg-gray-200 text-black"
                            }
                            ${
                              isWinnerCell
                                ? "ring-2 ring-yellow-500 shadow-[0_0_12px_rgba(250,204,21,0.9)] scale-105"
                                : ""
                            }`}
                                >
                                  {isFree ? "★" : num}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Button */}
                <button
                  onClick={() => {
                    setWinnerResult(null);
                    setIsGameEnded(false);
                    navigate("/");
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg font-serif"
                >
                  Play again 🔁
                </button>
              </div>
            </div>
          );
        })()}

      <Footer />
    </div>
  );
}
